import { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Heading,
  IconButton,
  Image,
  Input,
  Link,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Switch,
  Text,
  Textarea,
  Tooltip,
  Wrap,
  WrapItem,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { ArrowBackIcon, CloseIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import AvatarEditorModal from "../components/AvatarEditorModal";
import BioEditor from "../components/BioEditor";
import BioView from "../components/BioView";
import LinkListEditor from "../components/LinkListEditor";
import {
  editableLinks,
  parseLinks,
  toLegacyStrings,
  type ProfileLink,
} from "../lib/profileLinks";
import ConfirmDeleteDialog from "../components/ConfirmDeleteDialog";
import { useNavigate, useParams } from "react-router-dom";
import {
  CREATOR_STATUSES,
  CreatorsApi,
  type CreatorDetail as CreatorDetailT,
  type CreatorPatch,
  type CreatorProfileRow,
  type CreatorWorkRow,
} from "../api/creators";
import { EventsApi, type EventRow } from "../api/events";
import { OriginsApi, type OriginRow } from "../api/origins";
import { ReferralTermsFields } from "../components/ReferralTermsFields";
import { customerProfileBaseUrl } from "../auth/authConfig";
import { getEnv } from "../hooks/useEnv";
import { statusColor } from "../utils/certStatus";
import { fmtDate } from "../utils/datetime";
import logger from "../analytics/logger";
import { creatorSaveError } from "../lib/creatorSaveError";
import ReasonField from "../components/reason/ReasonField";

function clone<T>(v: T): T {
  return typeof structuredClone === "function"
    ? structuredClone(v)
    : (JSON.parse(JSON.stringify(v)) as T);
}

function eq(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

// Older creator records stored location as JSON like
//   {"country":"United States","state":"Washington","city":"Auburn"}
// Display as plain text and convert on next edit/save.
function formatLocation(loc: string | null | undefined): string {
  if (!loc) return "";
  const trimmed = loc.trim();
  if (!trimmed.startsWith("{")) return trimmed;
  try {
    const obj = JSON.parse(trimmed);
    if (obj && typeof obj === "object") {
      const parts = [obj.city, obj.state, obj.country]
        .filter((v): v is string => typeof v === "string" && v.trim() !== "")
        .map((v) => v.trim());
      if (parts.length) return parts.join(", ");
    }
  } catch {
    // not JSON — fall through
  }
  return trimmed;
}

function diff(orig: CreatorDetailT, draft: CreatorDetailT): CreatorPatch {
  const patch: CreatorPatch = {};
  if (!eq(orig.status, draft.status)) patch.status = draft.status;
  if (!eq(orig.verified, draft.verified)) patch.verified = draft.verified;
  if (!eq(orig.credits, draft.credits)) patch.credits = draft.credits;
  if (!eq(orig.referral_terms, draft.referral_terms))
    patch.referral_terms = draft.referral_terms;
  if (!eq(orig.tagline, draft.tagline))
    patch.tagline = draft.tagline;
  if (!eq(orig.bio, draft.bio)) patch.bio = draft.bio;
  if (!eq(orig.description, draft.description)) patch.description = draft.description;
  if (!eq(orig.profile_location, draft.profile_location)) patch.profile_location = draft.profile_location;
  if (!eq(orig.custom_link, draft.custom_link)) patch.custom_link = draft.custom_link;
  if (!eq(orig.profile_picture, draft.profile_picture)) patch.profile_picture = draft.profile_picture;
  if (!eq(orig.profile_types, draft.profile_types))
    patch.profile_types = draft.profile_types;
  // Links: compare and send the NORMALIZED shape. Comparing the legacy strings
  // would report a change whenever the encoding differs by whitespace for the
  // same link. The backend accepts either field and dual-writes both columns.
  for (const [newField, legacyField] of [
    ["social_links", "socials"],
    ["links", "other_links"],
  ] as const) {
    const before = parseLinks(orig[newField] ?? orig[legacyField]);
    const after = parseLinks(draft[newField] ?? draft[legacyField]);
    if (!eq(before, after)) {
      patch[newField] = after;
    }
  }
  if (!eq(orig.creative_styles, draft.creative_styles))
    patch.creative_styles = draft.creative_styles;

  const pii: NonNullable<CreatorPatch["pii"]> = {};
  if (!eq(orig.pii.name, draft.pii.name) && draft.pii.name)
    pii.name = draft.pii.name;
  if (!eq(orig.pii.email, draft.pii.email) && draft.pii.email)
    pii.email = draft.pii.email;
  if (
    !eq(orig.pii.name_searchable, draft.pii.name_searchable) &&
    draft.pii.name_searchable !== null
  )
    pii.name_searchable = draft.pii.name_searchable;
  if (
    !eq(orig.pii.email_searchable, draft.pii.email_searchable) &&
    draft.pii.email_searchable !== null
  )
    pii.email_searchable = draft.pii.email_searchable;
  if (Object.keys(pii).length) patch.pii = pii;

  if (!eq(orig.profiles, draft.profiles)) patch.profiles = draft.profiles;
  return patch;
}

function StringListEditor({
  values,
  onChange,
  placeholder,
}: {
  values: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
}) {
  return (
    <Stack spacing={1.5}>
      {values.map((v, i) => (
        <HStack key={i} spacing={1}>
          <Input
            size="sm"
            value={v}
            placeholder={placeholder}
            onChange={(e) => {
              const next = [...values];
              next[i] = e.target.value;
              onChange(next);
            }}
          />
          <IconButton
            size="xs"
            variant="ghost"
            aria-label="Remove"
            icon={<CloseIcon boxSize={2} />}
            onClick={() => onChange(values.filter((_, idx) => idx !== i))}
          />
        </HStack>
      ))}
      <Button size="xs" variant="ghost" alignSelf="flex-start" onClick={() => onChange([...values, ""])}>
        + Add
      </Button>
    </Stack>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Text
        fontWeight="semibold"
        fontSize="xs"
        textTransform="uppercase"
        letterSpacing="wider"
        color="gray.500"
        mb={1.5}
      >
        {title}
      </Text>
      <Box fontSize="sm">{children}</Box>
    </Box>
  );
}

function Empty() {
  return <Text color="gray.400">—</Text>;
}

// Accent color per sign-in provider for the identity-card badge. Unknown
// providers fall back to gray.
const SIGN_IN_COLOR_SCHEMES: Record<string, string> = {
  Email: "green",
  Google: "red",
  Apple: "gray",
  Facebook: "facebook",
};

// Shared 1px black outline applied to every pill on the identity badge line so
// Status / Verified / origin-event / work count / sign-in all read as one set.
const BADGE_BORDER = { borderWidth: "1px", borderColor: "black" } as const;

function TagCloud({ values }: { values: string[] }) {
  if (!values.length) return <Empty />;
  return (
    <Wrap spacing={1.5}>
      {values.map((v, i) => (
        <WrapItem key={`${v}-${i}`}>
          <Badge variant="subtle" textTransform="none" px={2} py={0.5} borderRadius="md">
            {v}
          </Badge>
        </WrapItem>
      ))}
    </Wrap>
  );
}

// One profile's works. `works === null` means still loading. No `as {profile_name}`
// suffix any more — the panel a row sits in already says whose profile it is.
function WorksList({
  works,
  onOpen,
}: {
  works: CreatorWorkRow[] | null;
  onOpen: (workId: string) => void;
}) {
  return (
    <Box>
      <Text
        fontWeight="semibold"
        fontSize="xs"
        textTransform="uppercase"
        letterSpacing="wider"
        color="gray.500"
        mb={2}
      >
        Works ({works?.length ?? 0})
      </Text>
      {works === null ? (
        <Box py={4} textAlign="center" color="gray.400">
          <Spinner size="sm" />
        </Box>
      ) : works.length === 0 ? (
        <Box p={4} borderWidth="1px" borderRadius="md" borderStyle="dashed">
          <Empty />
        </Box>
      ) : (
        <Stack spacing={1}>
          {works.map((w) => (
            <Flex
              key={w.id}
              p={3}
              borderWidth="1px"
              borderRadius="md"
              bg="white"
              cursor="pointer"
              _hover={{ bg: "gray.50" }}
              onClick={() => onOpen(w.id)}
              align="center"
              wrap="wrap"
              gap={2}
            >
              <Text
                fontSize="sm"
                fontWeight="medium"
                flex="1"
                minW="200px"
                wordBreak="break-word"
              >
                {w.title || "(untitled)"}
              </Text>
              <Badge colorScheme={statusColor(w.status)} textTransform="none">
                {w.status ?? "UNCERTIFIED"}
              </Badge>
              <Badge variant="outline" textTransform="none">
                {w.type}
              </Badge>
              <Badge variant="outline" textTransform="none">
                {w.format}
              </Badge>
            </Flex>
          ))}
        </Stack>
      )}
    </Box>
  );
}

/** Read-mode links. `values` may hold either legacy shape -- JSON strings from
 *  customer-app, bare URLs from the CSV import -- or normalized objects, so it
 *  goes through `parseLinks` rather than printing the stored value verbatim.
 *  Printing it verbatim is what used to show operators raw JSON. */
function LinkList({ values }: { values: unknown }) {
  const links = parseLinks(values);
  if (!links.length) return <Empty />;
  return (
    <Stack spacing={0.5}>
      {links.map((link, i) => (
        <Text key={i} fontSize="sm" wordBreak="break-all">
          {link.name ? `${link.name} — ` : ""}
          <Link href={link.url} isExternal color="blue.500">
            {link.url}
          </Link>
        </Text>
      ))}
    </Stack>
  );
}

export default function CreatorDetail() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<CreatorDetailT | null>(null);
  const [draft, setDraft] = useState<CreatorDetailT | null>(null);
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Which field the last rejected save blamed, so its control can be outlined.
  // Cleared as soon as that field is edited — a stale red border is worse than
  // none, because it points at something the operator has already changed.
  const [errorField, setErrorField] = useState<string | null>(null);
  const [works, setWorks] = useState<CreatorWorkRow[] | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  // Which profile panel is expanded (-1 = all collapsed), and which profile the
  // header avatar is showing. They differ only on collapse: a collapse is a
  // navigation gesture, not a "clear", so the avatar keeps the last-open profile.
  const [openProfileIdx, setOpenProfileIdx] = useState(0);
  const [shownProfileIdx, setShownProfileIdx] = useState(0);
  // Staged-avatar preview while editing: undefined = no pending change (show the
  // saved picture), a string = pending upload (object URL), null = pending removal.
  const [avatarPreview, setAvatarPreview] = useState<string | null | undefined>(undefined);
  const avatarModal = useDisclosure();
  const avatarConfirm = useDisclosure();
  const toast = useToast();

  const [origins, setOrigins] = useState<OriginRow[]>([]);
  const [originEvents, setOriginEvents] = useState<EventRow[]>([]);
  const [selectedOriginId, setSelectedOriginId] = useState<string>("");
  const [selectedEventId, setSelectedEventId] = useState<string>(""); // "" until the origin's events load

  // Upload the bytes to S3 now (to get a key), but only STAGE the key into the
  // draft — it is persisted via the Save PATCH, so the change is held until Save,
  // gated by the reason, and audited. Nothing is committed to the creator here.
  const uploadAvatar = async (blob: Blob) => {
    if (!data) return;
    setUploadingAvatar(true);
    try {
      const { uploadUrl, key } = await CreatorsApi.getAvatarUploadUrl(data.id, "image/png");
      const put = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": "image/png" },
        body: blob,
      });
      if (!put.ok) throw new Error(`upload_failed_${put.status}`);
      setField("profile_picture", key);
      setAvatarPreview(URL.createObjectURL(blob));
      toast({ title: "Avatar staged — Save to apply", status: "info", duration: 3000, isClosable: true });
      avatarModal.onClose();
    } catch (e) {
      logger.error("avatar_upload_failed", e);
      toast({
        title: "Avatar upload failed",
        description: (e as { message?: string })?.message ?? "Unknown error",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
      throw e;
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Stage removal into the draft; persisted (and audited) on Save.
  const deleteAvatar = () => {
    setField("profile_picture", null);
    setAvatarPreview(null);
    avatarConfirm.onClose();
  };

  // Reset load state immediately when the creator id changes; the fetch below
  // (re)populates it. Done at render time to avoid a synchronous setState effect.
  const [prevId, setPrevId] = useState(id);
  if (id !== prevId) {
    setPrevId(id);
    setLoading(true);
    setError(null);
    setWorks(null);
    // A different creator means a different profile list — reopen at the first.
    setOpenProfileIdx(0);
    setShownProfileIdx(0);
  }

  useEffect(() => {
    CreatorsApi.get(id)
      .then((d) => {
        setData(d);
        setDraft(null);
        setEditing(false);
      })
      .catch((e) => {
        logger.error("creator_get_failed", e);
        setError(e?.response?.data?.error ?? "Failed to load creator");
      })
      .finally(() => setLoading(false));
    CreatorsApi.works(id)
      .then((r) => setWorks(r.works))
      .catch((e) => logger.error("creator_works_failed", e));
  }, [id]);

  useEffect(() => {
    OriginsApi.list({ page: 1, page_size: 200 })
      .then((r) => setOrigins(r.origins))
      .catch((e) => logger.error("creator_origins_failed", e));
  }, []);

  // Initialize/reconcile the origin+event selects from the loaded creator
  // whenever the creator record or the origins list changes (e.g. after save,
  // or once origins finish loading). Done at render time to avoid a synchronous
  // setState effect.
  const [prevReconcileData, setPrevReconcileData] = useState(data);
  const [prevReconcileOrigins, setPrevReconcileOrigins] = useState(origins);
  if (data !== prevReconcileData || origins !== prevReconcileOrigins) {
    setPrevReconcileData(data);
    setPrevReconcileOrigins(origins);
    if (data && origins.length > 0) {
      const match = origins.find((o) => o.slug === data.origin);
      setSelectedOriginId(match?.id ?? "");
      setSelectedEventId(data.origin_event_id ?? "");
    }
  }

  // Fetch the selected origin's events while editing. The Default event is
  // included in the list like any other event. Reconcile the selection: keep
  // the creator's current event if it belongs to this origin, otherwise fall
  // back to this origin's Default row (every creator stays attributed).
  // Clear the loaded events when we leave edit mode or have no origin selected.
  // Done at render time to avoid a synchronous setState effect.
  const canLoadOriginEvents = editing && !!selectedOriginId;
  const [prevCanLoadOriginEvents, setPrevCanLoadOriginEvents] = useState(canLoadOriginEvents);
  if (canLoadOriginEvents !== prevCanLoadOriginEvents) {
    setPrevCanLoadOriginEvents(canLoadOriginEvents);
    if (!canLoadOriginEvents) setOriginEvents([]);
  }

  useEffect(() => {
    if (!editing || !selectedOriginId) return;
    EventsApi.list({ page: 1, origin_id: selectedOriginId })
      .then((r) => {
        setOriginEvents(r.events);
        setSelectedEventId((cur) => {
          if (cur && r.events.some((ev) => ev.id === cur)) return cur;
          const def = r.events.find((ev) => ev.name === "Default");
          return def ? def.id : "";
        });
      })
      .catch((e) => logger.error("creator_origin_events_failed", e));
  }, [selectedOriginId, editing]);

  const patch = useMemo(() => (data && draft ? diff(data, draft) : {}), [data, draft]);
  const selectedSlug = origins.find((o) => o.id === selectedOriginId)?.slug ?? null;
  const attributionPatch = useMemo(() => {
    const p: Partial<Pick<CreatorPatch, "origin" | "origin_event_id">> = {};
    if (!data) return p;
    if (selectedEventId) {
      // The Default event is a real, pickable row now, so a specific event id
      // (including the origin's Default) always attributes directly.
      if (selectedEventId !== data.origin_event_id) p.origin_event_id = selectedEventId;
    } else if (selectedSlug && selectedSlug !== data.origin) {
      // Degenerate fallback: origin has no resolvable Default event — route by
      // slug so the creator still stays linked to that origin's baseline.
      p.origin = selectedSlug;
    }
    return p;
  }, [data, selectedEventId, selectedSlug]);
  const dirty = Object.keys(patch).length > 0 || Object.keys(attributionPatch).length > 0;

  const startEdit = () => {
    if (!data) return;
    const d = clone(data);
    // Normalize location to plain text on edit so saving converts legacy JSON.
    d.profile_location = data.profile_location ? formatLocation(data.profile_location) || null : null;
    setDraft(d);
    setReason("");
    setAvatarPreview(undefined);
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(null);
    setReason("");
    setAvatarPreview(undefined);
    setEditing(false);
    setError(null);
    // Discard any unsaved origin/event selection back to the saved creator.
    if (data) {
      const match = origins.find((o) => o.slug === data.origin);
      setSelectedOriginId(match?.id ?? "");
      setSelectedEventId(data.origin_event_id ?? "");
    }
  };

  const goBack = () => {
    cancelEdit();
    navigate(-1);
  };

  const save = async () => {
    if (!data || !draft) return;
    if (!dirty) {
      setEditing(false);
      setDraft(null);
      return;
    }
    setSaving(true);
    setError(null);
    setErrorField(null);
    try {
      const finalPatch: CreatorPatch = { ...patch, ...attributionPatch };
      const updated = await CreatorsApi.update(data.id, finalPatch, { reason: reason.trim() });
      setData(updated);
      setDraft(null);
      setReason("");
      setAvatarPreview(undefined);
      setEditing(false);
    } catch (e) {
      logger.error("creator_update_failed", e);
      const { message, field } = creatorSaveError(e);
      setError(message);
      setErrorField(field);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box py={10} textAlign="center">
        <Spinner />
      </Box>
    );
  }
  if (error && !data) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }
  if (!data) return null;

  const view = editing && draft ? draft : data;
  // Public profile URL on the live customer site. The customer-app router
  // dual-resolves the trailing segment as the profile's vanity slug
  // (custom_link) or the profile's UUID — BOTH are creator_profile columns, so
  // this URL is only ever meaningful per profile, never per creator.
  const profileUrlPrefix = `${customerProfileBaseUrl(getEnv())}/profile/`;
  const profileUrlFor = (p: CreatorProfileRow) =>
    `${profileUrlPrefix}${p.custom_link || p.id}`;

  // Works belong to a profile (work.creator_profile_id), so they nest under one.
  // The endpoint inner-joins through creator_profile, so every row it returns is
  // guaranteed to match one of this creator's profiles — `unassignedWorks` is a
  // defensive bucket, not a path deleted-profile orphans can reach (those lose
  // their only link to the creator when the FK is nulled).
  const worksFor = (profileId: string) =>
    (works ?? []).filter((w) => w.creator_profile_id === profileId);
  const profileIds = new Set(view.profiles.map((p) => p.id));
  const unassignedWorks = (works ?? []).filter(
    (w) => !w.creator_profile_id || !profileIds.has(w.creator_profile_id),
  );

  // Only the primary (earliest) profile is editable: the PATCH contract writes
  // flattened profile fields through _primary_profile == profiles[0]. Renames
  // are the exception — those go through the `profiles` array, so every profile
  // can be renamed. Per-profile editing of the rest is a follow-on ticket.
  const PRIMARY = 0;

  const clearFieldError = (k: string) => {
    setErrorField((f) => (f === k ? null : f));
  };
  /** The draft's links as EDITOR ROWS, preferring the JSONB-backed field and
   *  falling back to the legacy one for a profile fetched before the API served
   *  it. Uses `editableLinks`, not `parseLinks`: these rows are re-derived on
   *  every render, and `parseLinks` would delete a freshly added blank row
   *  before it could be painted. */
  const draftLinks = (
    d: CreatorDetailT,
    newField: "links" | "social_links",
    legacyField: "other_links" | "socials",
  ): ProfileLink[] => editableLinks(d[newField], d[legacyField]);

  /** Write BOTH shapes into the draft so `diff()` and any other reader agree,
   *  regardless of which one they look at. */
  const setLinkField = (
    newField: "links" | "social_links",
    legacyField: "other_links" | "socials",
    next: ProfileLink[],
  ) => {
    clearFieldError(newField);
    setDraft((d) =>
      d ? { ...d, [newField]: next, [legacyField]: toLegacyStrings(next) } : d,
    );
  };

  const setField = <K extends keyof CreatorDetailT>(k: K, v: CreatorDetailT[K]) => {
    clearFieldError(k as string);
    setDraft((d) => (d ? { ...d, [k]: v } : d));
  };
  const setPII = <K extends keyof CreatorDetailT["pii"]>(
    k: K,
    v: CreatorDetailT["pii"][K],
  ) => {
    clearFieldError("pii");
    setDraft((d) => (d ? { ...d, pii: { ...d.pii, [k]: v } } : d));
  };

  return (
    <Box p={{ base: 4, md: 6 }} maxW="1100px" mx="auto">
      {/* Toolbar */}
      <Flex align="center" mb={4}>
        <IconButton
          aria-label="Back"
          icon={<ArrowBackIcon />}
          variant="ghost"
          size="sm"
          onClick={goBack}
          mr={2}
        />
        <Text fontSize="sm" color="gray.500" flex="1">
          Creator
        </Text>
        {editing ? (
          <HStack>
            <Button size="sm" onClick={cancelEdit} variant="ghost" isDisabled={saving}>
              Cancel
            </Button>
            <Button
              size="sm"
              colorScheme="blue"
              onClick={save}
              isLoading={saving}
              isDisabled={!dirty || !reason.trim()}
            >
              Save
            </Button>
          </HStack>
        ) : (
          <HStack spacing={2}>
            <Text fontSize="sm" color="gray.600">
              Edit
            </Text>
            <Switch size="sm" isChecked={editing} onChange={startEdit} />
          </HStack>
        )}
      </Flex>

      {error && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      )}

      {/* Identity card */}
      <Flex
        align="flex-start"
        gap={5}
        p={5}
        borderWidth="1px"
        borderRadius="lg"
        bg="white"
        mb={4}
      >
        <Box position="relative" flexShrink={0}>
          {(() => {
            // profile_picture is a creator_profile column, so the single header
            // avatar shows whichever profile is open below. A staged change
            // (string = pending upload, null = pending removal, undefined = no
            // change) only ever applies to the primary profile — that is the
            // only one the avatar PATCH can write.
            const shown = view.profiles[shownProfileIdx];
            const src =
              avatarPreview !== undefined && shownProfileIdx === PRIMARY
                ? avatarPreview
                : shown
                  ? shown.profile_picture_url
                  : view.profile_picture_url;
            return src ? (
              <Image
                src={src}
                alt={view.pii.name ?? "Profile"}
                boxSize="96px"
                borderRadius="full"
                objectFit="cover"
              />
            ) : (
              <Avatar size="xl" name={view.pii.name ?? undefined} />
            );
          })()}
          {/* Avatar edit affordances only while the PRIMARY profile is shown —
              the upload PATCH writes profiles[0], so offering them over another
              profile's picture would silently edit the wrong row. */}
          {editing && shownProfileIdx === PRIMARY && (
            <HStack
              spacing={1}
              position="absolute"
              bottom={-1}
              right={-1}
              bg="white"
              borderRadius="full"
              boxShadow="md"
              p={1}
            >
              <IconButton
                aria-label="Edit avatar"
                icon={<EditIcon />}
                size="xs"
                variant="ghost"
                onClick={avatarModal.onOpen}
                isDisabled={uploadingAvatar}
              />
              {view.profile_picture && (
                <IconButton
                  aria-label="Remove avatar"
                  icon={<DeleteIcon />}
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  onClick={avatarConfirm.onOpen}
                  isDisabled={uploadingAvatar}
                />
              )}
            </HStack>
          )}
        </Box>
        <AvatarEditorModal
          isOpen={avatarModal.isOpen}
          onClose={avatarModal.onClose}
          onCommit={uploadAvatar}
          busy={uploadingAvatar}
        />
        <ConfirmDeleteDialog
          isOpen={avatarConfirm.isOpen}
          onClose={avatarConfirm.onClose}
          onConfirm={deleteAvatar}
          count={1}
          entity="avatar"
          busy={false}
        />


        <Box flex="1" minW={0}>
          {editing && draft ? (
            <Stack spacing={2}>
              <Input
                size="md"
                fontWeight="semibold"
                value={draft.pii.name ?? ""}
                placeholder="Name"
                onChange={(e) => setPII("name", e.target.value)}
              />
              <Input
                size="sm"
                value={draft.pii.email ?? ""}
                placeholder="Email"
                onChange={(e) => setPII("email", e.target.value)}
              />
              <HStack spacing={3} wrap="wrap">
                <Select
                  size="sm"
                  isInvalid={errorField === "status"}
                  value={draft.status}
                  onChange={(e) => setField("status", e.target.value)}
                  maxW="240px"
                >
                  {CREATOR_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
                <Select
                  size="sm"
                  maxW="220px"
                  placeholder="Select origin"
                  value={selectedOriginId}
                  onChange={(e) => {
                    setSelectedOriginId(e.target.value);
                    setSelectedEventId("");
                  }}
                >
                  {origins.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.display_name ? `${o.display_name} (${o.slug})` : o.slug}
                    </option>
                  ))}
                </Select>
                <Select
                  size="sm"
                  maxW="220px"
                  value={selectedEventId}
                  onChange={(e) => setSelectedEventId(e.target.value)}
                  isDisabled={!selectedOriginId}
                >
                  {originEvents.map((ev) => (
                    <option key={ev.id} value={ev.id}>
                      {ev.name ?? "(unnamed)"}
                    </option>
                  ))}
                </Select>
                <HStack spacing={2} pl={1}>
                  <Text fontSize="sm" color="gray.600">
                    Verified
                  </Text>
                  <Switch
                    size="sm"
                    isChecked={!!draft.verified}
                    onChange={(e) => setField("verified", e.target.checked)}
                  />
                </HStack>
              </HStack>
              <HStack spacing={4} pt={1}>
                <Checkbox
                  size="sm"
                  isChecked={!!draft.pii.name_searchable}
                  onChange={(e) => setPII("name_searchable", e.target.checked)}
                >
                  Name searchable
                </Checkbox>
                <Checkbox
                  size="sm"
                  isChecked={!!draft.pii.email_searchable}
                  onChange={(e) => setPII("email_searchable", e.target.checked)}
                >
                  Email searchable
                </Checkbox>
              </HStack>
            </Stack>
          ) : (
            <Stack spacing={1}>
              <HStack spacing={2} align="center">
                <Heading size="lg" noOfLines={1}>
                  {view.pii.name ?? "(no name)"}
                </Heading>
                {view.pii.name_searchable && (
                  <Tooltip label="Name searchable">
                    <Badge colorScheme="green" variant="subtle">
                      N
                    </Badge>
                  </Tooltip>
                )}
              </HStack>
              <HStack spacing={2}>
                <Text color="gray.600" fontSize="sm">
                  {view.pii.email ?? "—"}
                </Text>
                {view.pii.email_searchable && (
                  <Tooltip label="Email searchable">
                    <Badge colorScheme="green" variant="subtle">
                      E
                    </Badge>
                  </Tooltip>
                )}
              </HStack>
              <HStack spacing={2} pt={2} wrap="wrap">
                <Badge colorScheme="purple" textTransform="none" {...BADGE_BORDER}>
                  {view.status}
                </Badge>
                {view.verified && (
                  <Badge colorScheme="green" textTransform="none" {...BADGE_BORDER}>
                    Verified
                  </Badge>
                )}
                {view.origin && (
                  <Badge {...BADGE_BORDER}>
                    {view.origin_event_name ? `${view.origin} — ${view.origin_event_name}` : view.origin}
                  </Badge>
                )}
                <Badge {...BADGE_BORDER}>
                  {view.work_count} {view.work_count === 1 ? "work" : "works"}
                </Badge>
                {view.sign_in_method && (
                  <Badge
                    colorScheme={SIGN_IN_COLOR_SCHEMES[view.sign_in_method] ?? "gray"}
                    textTransform="none"
                    {...BADGE_BORDER}
                  >
                    {view.sign_in_method}
                  </Badge>
                )}
              </HStack>
            </Stack>
          )}
        </Box>
      </Flex>

      {/* Body grid — creator-level facts only. Everything that lives on
          creator_profile moved into the per-profile accordion below. */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
        <Section title="Created">{fmtDate(data.created_at)}</Section>
        <Section title="Updated">{fmtDate(data.updated_at)}</Section>

        <Section title="Credits">
          {editing && draft ? (
            <Input
              size="sm"
              type="number"
              min={0}
              isInvalid={errorField === "credits"}
              value={draft.credits}
              onChange={(e) =>
                setField("credits", Math.max(0, Math.floor(Number(e.target.value) || 0)))
              }
            />
          ) : (
            view.credits
          )}
        </Section>
        <Section title="Referral terms">
          {/* Tier-1 (individual influencer) override — null inherits from this
              creator's origin_event, then General/Default. Never a cascade
              floor at this scope, so clearing is always allowed. */}
          <ReferralTermsFields
            value={view.referral_terms}
            onChange={(rt) => setField("referral_terms", rt)}
            editing={!!(editing && draft)}
            effective={data.referral_terms_effective}
          />
        </Section>
      </SimpleGrid>

      {/* Profiles — one accordion panel per creator_profile, one open at a time.
          Every field in here is a creator_profile column, so a creator with
          several profiles shows each one's own values. */}
      <Box mt={6}>
        <Text
          fontWeight="semibold"
          fontSize="xs"
          textTransform="uppercase"
          letterSpacing="wider"
          color="gray.500"
          mb={2}
        >
          Profiles ({view.profiles.length})
        </Text>
        {view.profiles.length === 0 && unassignedWorks.length === 0 ? (
          <Box p={4} borderWidth="1px" borderRadius="md" borderStyle="dashed">
            <Empty />
          </Box>
        ) : (
          <Accordion
            allowToggle
            index={openProfileIdx}
            onChange={(i) => {
              const next = i as number;
              setOpenProfileIdx(next);
              // Keep the avatar on the last real profile that was open; the
              // trailing "Unassigned" panel owns no profile picture.
              if (next >= 0 && next < view.profiles.length) setShownProfileIdx(next);
            }}
          >
            {view.profiles.map((p, i) => (
              <AccordionItem
                key={p.id}
                borderWidth="1px"
                borderRadius="md"
                mb={2}
                bg="white"
              >
                <AccordionButton>
                  <HStack flex="1" spacing={2} textAlign="left" minW={0}>
                    <Text fontSize="sm" fontWeight="semibold" wordBreak="break-word">
                      {p.profile_name || "(unnamed)"}
                    </Text>
                    {i === PRIMARY && (
                      <Badge variant="subtle" textTransform="none">
                        Primary
                      </Badge>
                    )}
                    <Badge variant="outline" textTransform="none">
                      {worksFor(p.id).length}{" "}
                      {worksFor(p.id).length === 1 ? "work" : "works"}
                    </Badge>
                  </HStack>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                    {editing && draft && (
                      <Section title="Profile name">
                        {/* Renames go through the `profiles` array, which the
                            PATCH accepts for EVERY profile — unlike the fields
                            below, which only reach the primary. */}
                        <Input
                          size="sm"
                          value={draft.profiles[i]?.profile_name ?? ""}
                          onChange={(e) => {
                            const next = [...draft.profiles];
                            next[i] = { ...next[i], profile_name: e.target.value };
                            setField("profiles", next);
                          }}
                        />
                      </Section>
                    )}
                    <Section title="Profile link">
                      {editing && draft && i === PRIMARY ? (
                        <>
                          <HStack spacing={0} align="center">
                            <Text
                              fontSize="sm"
                              color="gray.500"
                              whiteSpace="nowrap"
                              flexShrink={0}
                            >
                              {profileUrlPrefix}
                            </Text>
                            <Input
                              size="sm"
                              isInvalid={errorField === "custom_link"}
                              value={draft.custom_link ?? ""}
                              placeholder={p.id}
                              onChange={(e) =>
                                setField("custom_link", e.target.value || null)
                              }
                            />
                          </HStack>
                          {/* Mirrors _validate_custom_link in
                              backend/support/routes/creators.py. Shown while
                              editing so the four rejection reasons are
                              avoidable rather than only discoverable on a
                              failed Save. */}
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            3–50 characters. Letters, numbers and hyphens only
                            — capitals and surrounding spaces are normalized
                            away. Must be unique. Leave blank to use the
                            profile ID.
                          </Text>
                        </>
                      ) : (
                        <Link
                          href={profileUrlFor(p)}
                          isExternal
                          color="blue.500"
                          wordBreak="break-all"
                        >
                          {profileUrlFor(p)}
                        </Link>
                      )}
                    </Section>
                    <Section title="Location">
                      {editing && draft && i === PRIMARY ? (
                        <Input
                          size="sm"
                          value={draft.profile_location ?? ""}
                          onChange={(e) =>
                            setField("profile_location", e.target.value || null)
                          }
                        />
                      ) : p.profile_location ? (
                        formatLocation(p.profile_location) || <Empty />
                      ) : (
                        <Empty />
                      )}
                    </Section>

                    <Section title="Creator types">
                      {editing && draft && i === PRIMARY ? (
                        <StringListEditor
                          values={draft.profile_types}
                          onChange={(v) => setField("profile_types", v)}
                        />
                      ) : (
                        <TagCloud values={p.profile_types} />
                      )}
                    </Section>
                    <Section title="Tagline">
                      {editing && draft && i === PRIMARY ? (
                        <Textarea
                          size="sm"
                          rows={2}
                          value={draft.tagline ?? ""}
                          onChange={(e) => setField("tagline", e.target.value || null)}
                        />
                      ) : (
                        p.tagline ?? <Empty />
                      )}
                    </Section>

                    <Section title="Creative styles">
                      {editing && draft && i === PRIMARY ? (
                        <StringListEditor
                          values={draft.creative_styles}
                          onChange={(v) => setField("creative_styles", v)}
                        />
                      ) : (
                        <TagCloud values={p.creative_styles} />
                      )}
                    </Section>
                    <Section title="Description">
                      {editing && draft && i === PRIMARY ? (
                        <Textarea
                          size="sm"
                          rows={4}
                          value={draft.description ?? ""}
                          onChange={(e) =>
                            setField("description", e.target.value || null)
                          }
                        />
                      ) : p.description ? (
                        <Text whiteSpace="pre-wrap">{p.description}</Text>
                      ) : (
                        <Empty />
                      )}
                    </Section>

                    <Section title="Socials">
                      {editing && draft && i === PRIMARY ? (
                        <LinkListEditor
                          showName={false}
                          values={draftLinks(draft, "social_links", "socials")}
                          onChange={(v) => setLinkField("social_links", "socials", v)}
                        />
                      ) : (
                        <LinkList values={p.social_links ?? p.socials} />
                      )}
                    </Section>
                    <Section title="Bio">
                      {editing && draft && i === PRIMARY ? (
                        // `key` remounts the editor when the profile changes.
                        // BioEditor seeds its contentEditable once on mount, so
                        // without this it would keep showing the previous bio.
                        <BioEditor
                          key={`bio-${p.id}`}
                          value={draft.bio}
                          onChange={(md) => setField("bio", md || null)}
                        />
                      ) : p.bio ? (
                        <BioView bio={p.bio} />
                      ) : (
                        <Empty />
                      )}
                    </Section>

                    <Section title="Other links">
                      {editing && draft && i === PRIMARY ? (
                        <LinkListEditor
                          values={draftLinks(draft, "links", "other_links")}
                          onChange={(v) => setLinkField("links", "other_links", v)}
                        />
                      ) : (
                        <LinkList values={p.links ?? p.other_links} />
                      )}
                    </Section>
                    <Section title="Profile picture id">
                      {p.profile_picture ? (
                        <Text
                          fontFamily="mono"
                          fontSize="xs"
                          wordBreak="break-all"
                          color="gray.600"
                        >
                          {p.profile_picture}
                        </Text>
                      ) : (
                        <Empty />
                      )}
                    </Section>

                    <Section title="Created">{fmtDate(p.created_at)}</Section>
                    <Section title="Updated">{fmtDate(p.updated_at)}</Section>
                  </SimpleGrid>

                  {editing && draft && i !== PRIMARY && (
                    <Text fontSize="xs" color="gray.500" mt={3}>
                      Only the primary profile's fields are editable here. Renaming
                      works for every profile.
                    </Text>
                  )}

                  <Box mt={5}>
                    <WorksList
                      works={works === null ? null : worksFor(p.id)}
                      onOpen={(wid) => navigate(`/works/${wid}`)}
                    />
                  </Box>
                </AccordionPanel>
              </AccordionItem>
            ))}

            {unassignedWorks.length > 0 && (
              <AccordionItem borderWidth="1px" borderRadius="md" mb={2} bg="white">
                <AccordionButton>
                  <HStack flex="1" spacing={2} textAlign="left">
                    <Text fontSize="sm" fontWeight="semibold" color="gray.600">
                      Unassigned
                    </Text>
                    <Badge variant="outline" textTransform="none">
                      {unassignedWorks.length}{" "}
                      {unassignedWorks.length === 1 ? "work" : "works"}
                    </Badge>
                  </HStack>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <Text fontSize="xs" color="gray.500" mb={3}>
                    Works with no owning profile.
                  </Text>
                  <WorksList
                    works={unassignedWorks}
                    onOpen={(wid) => navigate(`/works/${wid}`)}
                  />
                </AccordionPanel>
              </AccordionItem>
            )}
          </Accordion>
        )}
      </Box>

      {editing && draft && (
        <Box mt={4}>
          <ReasonField
            value={reason}
            onChange={(v) => {
              clearFieldError("reason");
              setReason(v);
            }}
            isInvalid={errorField === "reason"}
          />
        </Box>
      )}

    </Box>
  );
}
